import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'INSPIRATION', label: 'Inspiration' },
  { value: 'SKETCHING', label: 'Sketching' },
  { value: 'SKETCHED', label: 'Sketched' },
  { value: 'SOURCING', label: 'Sourcing' },
  { value: 'EXT_PRODUCTION', label: 'Ext Production' },
  { value: 'SAMPLING', label: 'Sampling' },
  { value: 'FINISHED_GOOD', label: 'Finished Good' },
  { value: 'ALTERATION', label: 'Alteration' },
  { value: 'ALTERED', label: 'Altered' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED_INSPIRATION', label: 'Rejected Inspiration' },
  { value: 'REJECTED_POST_SAMPLING', label: 'Rejected Post Sampling' },
  { value: 'REJECTED_SOURCING', label: 'Rejected Sourcing' }
];

const UOM_OPTIONS = [
  { value: 'in', label: 'Inches (in)' },
  { value: 'cm', label: 'Centimeters (cm)' }
];

const SIZE_OPTIONS = [
  { value: 'X-Small', label: 'X-Small' },
  { value: 'Small', label: 'Small' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Large', label: 'Large' },
  { value: 'X-Large', label: 'X-Large' }
];

export default function FashionDesignForm({ open, onClose, onSuccess, editingDesign }) {
  // Helper function to get display URL for images
  const getImageDisplayUrl = (imageData) => {
    if (!imageData) return '';
    // If it's base64, return as is
    if (imageData.startsWith('data:image')) return imageData;
    // If it's a file path, convert to full URL
    if (imageData.startsWith('images/')) return `${API}/${imageData}`;
    // Otherwise return as is
    return imageData;
  };

  // Error handling
  const [errorMessage, setErrorMessage] = useState('');

  // Section 1: Article Selection
  const [articleCode, setArticleCode] = useState('');
  const [articleCodes, setArticleCodes] = useState([]);
  const [category, setCategory] = useState('');
  const [articleBarcode, setArticleBarcode] = useState('');

  // Section 2: Fabrics
  const [fabrics, setFabrics] = useState([]);
  const [fabricCodes, setFabricCodes] = useState([]);
  const [fabricColors, setFabricColors] = useState({});

  // Section 3: Digital Design
  const [digitalDesignCode, setDigitalDesignCode] = useState('');
  const [digitalDesignCodes, setDigitalDesignCodes] = useState([]);
  const [digitalDesignColorway, setDigitalDesignColorway] = useState('');
  const [digitalDesignColorways, setDigitalDesignColorways] = useState([]);
  const [digitalDesignColorwayImage, setDigitalDesignColorwayImage] = useState(''); // Image for selected colorway
  const [digitalDesignData, setDigitalDesignData] = useState(null); // Store full design data for color lookup
  const [digitalDesignType, setDigitalDesignType] = useState('');
  const [digitalDesignConsumption, setDigitalDesignConsumption] = useState('');

  // Section 4: Status
  const [status, setStatus] = useState('INSPIRATION');
  const [initialStatus, setInitialStatus] = useState(''); // Track original status for FINISHED_GOOD restriction

  // Compute available status options based on FINISHED_GOOD restriction
  const availableStatusOptions = useMemo(() => {
    // If initial status was FINISHED_GOOD or current status is FINISHED_GOOD,
    // only allow FINISHED_GOOD, ALTERATION, ALTERED, APPROVED, or REJECTED_POST_SAMPLING
    if (initialStatus === 'FINISHED_GOOD' || status === 'FINISHED_GOOD') {
      return STATUS_OPTIONS.filter(opt => 
        opt.value === 'FINISHED_GOOD' ||
        opt.value === 'ALTERATION' || 
        opt.value === 'ALTERED' || 
        opt.value === 'APPROVED' ||
        opt.value === 'REJECTED_POST_SAMPLING'
      );
    }
    // Otherwise, show all status options
    return STATUS_OPTIONS;
  }, [initialStatus, status]);

  // Section 5: Points of Measurement
  const [pomSize, setPomSize] = useState('Small'); // Single size for all POMs
  const [poms, setPoms] = useState([]);

  // Section 6: Trims & Accessories
  const [trims, setTrims] = useState([]);
  const [trimCodes, setTrimCodes] = useState([]);
  const [trimColors, setTrimColors] = useState({});

  // Section 7: Images
  const [inspirationImage, setInspirationImage] = useState('');
  const [sketchFrontImage, setSketchFrontImage] = useState('');
  const [sketchBackImage, setSketchBackImage] = useState('');
  const [finishedGoodImage, setFinishedGoodImage] = useState('');

  // Section 8: Comments
  const [comments, setComments] = useState('');
  
  // Tech Pack PDF - preserve existing PDF
  const [techPackPdf, setTechPackPdf] = useState(null);

  useEffect(() => {
    if (open) {
      fetchArticleCodes();
      fetchFabricCodes();
      fetchDigitalDesignCodes();
      fetchTrimCodes();
      
      if (editingDesign) {
        loadEditingDesign(editingDesign);
      } else {
        resetForm();
      }
    }
  }, [open, editingDesign]);

  // Debug: Log when colorways state changes
  useEffect(() => {
    console.log('>>> COLORWAYS STATE CHANGED <<<');
    console.log('digitalDesignColorways:', JSON.stringify(digitalDesignColorways, null, 2));
    console.log('Number of options:', digitalDesignColorways.length);
  }, [digitalDesignColorways]);

  const resetForm = () => {
    setArticleCode('');
    setCategory('');
    setArticleBarcode('');
    setFabrics([]);
    setDigitalDesignCode('');
    setDigitalDesignColorway('');
    setDigitalDesignColorwayImage('');
    setDigitalDesignType('');
    setDigitalDesignConsumption('');
    setStatus('INSPIRATION');
    setInitialStatus(''); // Reset initial status for new designs
    setPomSize('Small');
    setPoms([]);
    setTrims([]);
    setInspirationImage('');
    setSketchFrontImage('');
    setSketchBackImage('');
    setFinishedGoodImage('');
    setComments('');
    setTechPackPdf(null); // Reset tech pack
  };

  const loadEditingDesign = async (design) => {
    setArticleCode(design.article_code);
    setCategory(design.category || '');
    setArticleBarcode(design.article_barcode || '');
    setFabrics(design.fabrics || []);
    setDigitalDesignCode(design.digital_design_code || '');
    setDigitalDesignColorway(design.digital_design_colorway || '');
    setDigitalDesignColorwayImage(design.digital_design_colorway_image || '');
    setDigitalDesignType(design.digital_design_type || '');
    setDigitalDesignConsumption(design.digital_design_consumption || '');
    const designStatus = design.status || 'INSPIRATION';
    setStatus(designStatus);
    setInitialStatus(designStatus); // Track initial status for FINISHED_GOOD restriction
    // Load size from first POM if exists
    setPomSize((design.poms && design.poms.length > 0) ? design.poms[0].size : 'Small');
    setPoms(design.poms || []);
    setTrims(design.trims || []);
    // Load images - keep original paths if they exist (for edit), or empty for new upload
    setInspirationImage(design.inspiration_image_path || '');
    setSketchFrontImage(design.sketch_front_image_path || '');
    setSketchBackImage(design.sketch_back_image_path || '');
    setFinishedGoodImage(design.finished_good_image_path || '');
    setComments(design.comments || '');
    setTechPackPdf(design.tech_pack_pdf || null); // CRITICAL: Load tech pack PDF

    // CRITICAL FIX: Fetch dependent dropdown data when editing
    const token = localStorage.getItem('token');
    
    // Fetch fabric colors for each fabric
    if (design.fabrics && design.fabrics.length > 0) {
      const fabricColorsMap = {};
      for (let i = 0; i < design.fabrics.length; i++) {
        const fabric = design.fabrics[i];
        if (fabric.fabric_code) {
          try {
            const response = await axios.get(`${API}/master-data/colors/FABRIC LN/${fabric.fabric_code}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            fabricColorsMap[i] = response.data;
          } catch (error) {
            console.error(`Error fetching colors for fabric ${fabric.fabric_code}:`, error);
          }
        }
      }
      setFabricColors(fabricColorsMap);
    }

    // Fetch digital design colorways if design has digital design code
    if (design.digital_design_code) {
      try {
        const response = await axios.get(`${API}/digital-designs-master`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const digitalDesign = response.data.find(d => d.ln_t_code === design.digital_design_code);
        if (digitalDesign) {
          const designType = digitalDesign.design_type?.toUpperCase();
          if (designType === 'PRINT' && digitalDesign.colors && Array.isArray(digitalDesign.colors) && digitalDesign.colors.length > 0) {
            const colorOptions = digitalDesign.colors.map(c => ({
              value: c.color_code,
              label: `${c.color_code} - ${c.color_name}`
            }));
            setDigitalDesignColorways(colorOptions);
          } else if (designType === 'EMBROIDERY') {
            setDigitalDesignColorways([]);
          } else {
            const colorways = digitalDesign.colorways || [];
            setDigitalDesignColorways(colorways.map(c => ({
              value: c.colorway_name || c,
              label: c.colorway_name || c
            })));
          }
          setDigitalDesignData(digitalDesign);
        }
      } catch (error) {
        console.error('Error fetching digital design colorways:', error);
      }
    }

    // Fetch trim colors for each trim
    if (design.trims && design.trims.length > 0) {
      const trimColorsMap = {};
      for (let i = 0; i < design.trims.length; i++) {
        const trim = design.trims[i];
        if (trim.trim_code) {
          try {
            const response = await axios.get(`${API}/master-data/colors/TRIMS AND ACCESSORIES LN-Z/${trim.trim_code}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            trimColorsMap[i] = response.data;
          } catch (error) {
            console.error(`Error fetching colors for trim ${trim.trim_code}:`, error);
          }
        }
      }
      setTrimColors(trimColorsMap);
    }
  };

  // Fetch Functions
  const fetchArticleCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch from articles_master collection
      const response = await axios.get(`${API}/articles-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Article codes fetched:', response.data);
      setArticleCodes(response.data.map(a => ({ 
        value: a.ln_x_code, 
        label: `${a.ln_x_code}${a.article_category ? ' - ' + a.article_category : ''}` 
      })));
    } catch (error) {
      console.error('Error fetching article codes:', error);
      toast.error('Failed to fetch article codes');
    }
  };

  const fetchFabricCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/codes/FABRIC LN`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFabricCodes(response.data.map(f => ({ value: f.code, label: f.code })));
    } catch (error) {
      console.error('Error fetching fabric codes:', error);
    }
  };

  const fetchDigitalDesignCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch from digital_designs_master collection
      const response = await axios.get(`${API}/digital-designs-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Digital design codes fetched:', response.data);
      
      setDigitalDesignCodes(response.data.map(d => ({ 
        value: d.ln_t_code, 
        label: d.ln_t_code 
      })));
    } catch (error) {
      console.error('Error fetching digital design codes:', error);
      toast.error('Failed to fetch digital design codes');
    }
  };

  const fetchTrimCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/codes/TRIMS AND ACCESSORIES LN-Z`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrimCodes(response.data.map(t => ({ value: t.code, label: t.code })));
    } catch (error) {
      console.error('Error fetching trim codes:', error);
    }
  };

  // Article Selection Handler
  const handleArticleCodeChange = async (code) => {
    setArticleCode(code);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/articles-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const article = response.data.find(a => a.ln_x_code === code);
      if (article) {
        setCategory(article.article_category || '');
        setArticleBarcode(article.barcode?.image || '');
      }
    } catch (error) {
      console.error('Error fetching article details:', error);
    }
  };

  // Fabric Handlers
  const addFabric = () => {
    setFabrics([...fabrics, {
      fabric_code: '',
      color_code: '',
      color_name: '',
      width: 0,
      gsm: 0,
      composition: '',
      fabric_type: '',
      consumption: 0
    }]);
  };

  const removeFabric = (index) => {
    setFabrics(fabrics.filter((_, i) => i !== index));
  };

  const updateFabric = (index, field, value) => {
    const updated = [...fabrics];
    updated[index][field] = value;
    setFabrics(updated);
  };

  const handleFabricCodeChange = async (index, code) => {
    updateFabric(index, 'fabric_code', code);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/colors/FABRIC LN/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFabricColors({ ...fabricColors, [index]: response.data });
      
      // Fetch fabric details
      const fabricResponse = await axios.get(`${API}/master-data/by-code/FABRIC LN/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (fabricResponse.data) {
        updateFabric(index, 'width', fabricResponse.data.width || 0);
        updateFabric(index, 'gsm', fabricResponse.data.gsm || 0);
        updateFabric(index, 'composition', fabricResponse.data.composition || '');
        updateFabric(index, 'fabric_type', fabricResponse.data.fabric_type || '');
      }
    } catch (error) {
      console.error('Error fetching fabric details:', error);
    }
  };

  const handleFabricColorChange = (index, colorCode) => {
    const colors = fabricColors[index] || [];
    const color = colors.find(c => c.color_code === colorCode);
    
    updateFabric(index, 'color_code', colorCode);
    updateFabric(index, 'color_name', color?.color_name || '');
    updateFabric(index, 'price_per_meter', color?.price_per_meter || 0);
  };

  // Digital Design Handlers
  const handleDigitalDesignColorwayChange = (colorCode) => {
    console.log('=== COLORWAY CHANGE - FETCHING IMAGE ===');
    console.log('Selected colorway code:', colorCode);
    setDigitalDesignColorway(colorCode);
    
    // Find and set the image for the selected colorway
    if (digitalDesignData && digitalDesignData.colors && Array.isArray(digitalDesignData.colors)) {
      const selectedColor = digitalDesignData.colors.find(c => c.color_code === colorCode);
      console.log('Found color data:', selectedColor);
      
      if (selectedColor && selectedColor.image_path) {
        console.log('Setting colorway image from file path');
        setDigitalDesignColorwayImage(`${API}/${selectedColor.image_path}`);
      } else {
        console.log('No image found for this colorway');
        setDigitalDesignColorwayImage('');
      }
    } else {
      console.log('No design data available for image lookup');
      setDigitalDesignColorwayImage('');
    }
    console.log('=== END COLORWAY CHANGE ===');
  };

  const handleDigitalDesignCodeChange = async (code) => {
    console.log('=== DIGITAL DESIGN COLORWAY FETCH DEBUG ===');
    console.log('1. Selected code:', code);
    setDigitalDesignCode(code);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/digital-designs-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('2. Total designs fetched:', response.data.length);
      console.log('3. All design codes available:', response.data.map(d => d.ln_t_code));
      
      const design = response.data.find(d => d.ln_t_code === code);
      console.log('4. Found design match:', design ? 'YES' : 'NO');
      console.log('5. Full design object:', JSON.stringify(design, null, 2));
      
      if (design) {
        console.log('6. Design type:', design.design_type);
        console.log('7. Design colors exists?', design.colors ? 'YES' : 'NO');
        console.log('8. Design colors is array?', Array.isArray(design.colors) ? 'YES' : 'NO');
        console.log('9. Design colors length:', design.colors?.length || 0);
        console.log('10. Design colors content:', JSON.stringify(design.colors, null, 2));
        
        // For PRINT type, get colorways from colors array (handle both uppercase and lowercase)
        const designType = design.design_type?.toUpperCase();
        if (designType === 'PRINT' && design.colors && Array.isArray(design.colors) && design.colors.length > 0) {
          console.log('11. CONDITION MET: Processing PRINT colors');
          const colorOptions = design.colors.map(c => {
            console.log('   - Mapping color:', c);
            return { 
              value: c.color_code, 
              label: `${c.color_code} - ${c.color_name}` 
            };
          });
          console.log('12. Color options created:', JSON.stringify(colorOptions, null, 2));
          setDigitalDesignColorways(colorOptions);
          console.log('13. State updated with', colorOptions.length, 'color options');
        } else if (designType === 'EMBROIDERY') {
          // For EMBROIDERY, no colorways needed
          console.log('11. CONDITION MET: EMBROIDERY type - no colors needed');
          setDigitalDesignColorways([]);
        } else {
          console.log('11. FALLBACK: No design_type or using old colorways field');
          console.log('    - design_type value:', design.design_type);
          console.log('    - Has colorways field?', design.colorways ? 'YES' : 'NO');
          // Fallback for old data that might have colorways field
          const colorways = design.colorways || [];
          setDigitalDesignColorways(colorways.map(c => ({ 
            value: c.colorway_name || c, 
            label: c.colorway_name || c 
          })));
        }
        
        setDigitalDesignType(design.design_type || 'N/A');
        setDigitalDesignConsumption(''); // Consumption not stored in digital design master
        setDigitalDesignData(design); // Store full design data for colorway image lookup
        console.log('14. Design type and category set');
        console.log('15. Full design data stored for colorway lookup');
      } else {
        console.log('ERROR: Design not found for code:', code);
        setDigitalDesignData(null);
      }
      console.log('=== END DIGITAL DESIGN DEBUG ===');
    } catch (error) {
      console.error('FATAL ERROR fetching digital design details:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to fetch digital design details');
    }
  };

  // POM Handlers
  const addPOM = () => {
    setPoms([...poms, { size: pomSize, point_name: '', measurement: 0, uom: 'in' }]);
  };

  const removePOM = (index) => {
    setPoms(poms.filter((_, i) => i !== index));
  };

  const updatePOM = (index, field, value) => {
    const updated = [...poms];
    updated[index][field] = value;
    setPoms(updated);
  };

  // Trim Handlers
  const addTrim = () => {
    setTrims([...trims, { trim_code: '', color_code: '', color_name: '', quantity: 0 }]);
  };

  const removeTrim = (index) => {
    setTrims(trims.filter((_, i) => i !== index));
  };

  const updateTrim = (index, field, value) => {
    const updated = [...trims];
    updated[index][field] = value;
    setTrims(updated);
  };

  const handleTrimCodeChange = async (index, code) => {
    updateTrim(index, 'trim_code', code);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/colors/TRIMS AND ACCESSORIES LN-Z/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrimColors({ ...trimColors, [index]: response.data });
    } catch (error) {
      console.error('Error fetching trim colors:', error);
    }
  };

  const handleTrimColorChange = (index, colorCode) => {
    const colors = trimColors[index] || [];
    const color = colors.find(c => c.color_code === colorCode);
    
    updateTrim(index, 'color_code', colorCode);
    updateTrim(index, 'color_name', color?.color_name || '');
    updateTrim(index, 'price', color?.price || 0);
  };

  // Image Upload Handler
  const handleImageUpload = (e, setImage) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!articleCode) {
      toast.error('Please select an article code');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const designData = {
        article_code: articleCode,
        category,
        fabrics,
        digital_design_code: digitalDesignCode || null,
        digital_design_colorway: digitalDesignColorway || null,
        digital_design_colorway_image: digitalDesignColorwayImage || null,
        digital_design_type: digitalDesignType || null,
        digital_design_consumption: digitalDesignConsumption || null,
        status,
        poms,
        trims,
        inspiration_image_path: inspirationImage || null,
        sketch_front_image_path: sketchFrontImage || null,
        sketch_back_image_path: sketchBackImage || null,
        finished_good_image_path: finishedGoodImage || null,
        comments: comments || null,
        // CRITICAL: Always preserve tech_pack_pdf from state
        tech_pack_pdf: techPackPdf
      };

      if (editingDesign) {
        await axios.put(`${API}/designs/${editingDesign.id}`, designData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Design updated successfully');
      } else {
        await axios.post(`${API}/designs`, designData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Design created successfully');
      }

      onSuccess();
      onClose();
      setErrorMessage('');
    } catch (error) {
      console.error('Error saving design:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to save design';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { onClose(); if (!isOpen) setErrorMessage(''); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader className="border-b-2 border-black pb-4">
          <DialogTitle className="text-xl font-bold tracking-tight uppercase">
            {editingDesign ? 'Edit Design' : 'New Design'}
          </DialogTitle>
        </DialogHeader>

        {errorMessage && (
          <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
              <button
                type="button"
                onClick={() => setErrorMessage('')}
                className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-1">
          {/* Section 1: Article Selection */}
          <div className="space-y-4 p-5 border-2 border-black">
            <h3 className="text-xs font-semibold tracking-wider text-gray-500">1. ARTICLE SELECTION</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">ARTICLE CODE (LN-X) *</Label>
                <SearchableSelect
                  options={articleCodes}
                  value={articleCode}
                  onChange={handleArticleCodeChange}
                  placeholder="Select article code"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">CATEGORY (AUTO-FILLED)</Label>
                <Input value={category} readOnly className="bg-gray-50 border-gray-300 text-sm" />
              </div>
            </div>
            {articleBarcode && (
              <div className="mt-4">
                <Label className="text-xs font-semibold text-gray-500">ARTICLE BARCODE</Label>
                <img src={articleBarcode} alt="Article Barcode" className="max-w-xs mt-2" />
              </div>
            )}
          </div>

          {/* Section 2: Add Fabrics */}
          <div className="space-y-4 p-5 border-2 border-black">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-semibold tracking-wider text-gray-500">2. ADD FABRICS</h3>
              <Button type="button" onClick={addFabric} className="gap-2 bg-black hover:bg-gray-800" size="sm">
                <Plus className="w-4 h-4" />
                Add Fabric
              </Button>
            </div>
            {fabrics.map((fabric, index) => (
              <div key={index} className="p-4 border border-gray-300 bg-gray-50 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold">Fabric {index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFabric(index)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">FABRIC CODE (LN)</Label>
                    <SearchableSelect
                      options={fabricCodes}
                      value={fabric.fabric_code}
                      onChange={(val) => handleFabricCodeChange(index, val)}
                      placeholder="Select fabric"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">COLOR CODE</Label>
                    <SearchableSelect
                      options={fabricColors[index]?.map(c => ({ 
                        value: c.color_code, 
                        label: `${c.color_code} - ${c.color_name}` 
                      })) || []}
                      value={fabric.color_code}
                      onChange={(val) => handleFabricColorChange(index, val)}
                      placeholder="Select color"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">CONSUMPTION (METERS)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={fabric.consumption || ''}
                      onChange={(e) => updateFabric(index, 'consumption', parseFloat(e.target.value) || 0)}
                      placeholder="Enter value"
                      className="text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">WIDTH (AUTO)</Label>
                    <Input value={fabric.width} readOnly className="bg-white border-gray-300 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">GSM (AUTO)</Label>
                    <Input value={fabric.gsm} readOnly className="bg-white border-gray-300 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">COMPOSITION (AUTO)</Label>
                    <Input value={fabric.composition} readOnly className="bg-white border-gray-300 text-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">TYPE (AUTO)</Label>
                    <Input value={fabric.fabric_type} readOnly className="bg-white border-gray-300 text-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Section 3: Digital Design */}
          <div className="space-y-4 p-5 border-2 border-black">
            <h3 className="text-xs font-semibold tracking-wider text-gray-500">3. DIGITAL DESIGN</h3>
            <div className="grid grid-cols-3 gap-4">
              {/* Left side - Text fields (2/3 width) */}
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">DIGITAL DESIGN CODE (LN-T)</Label>
                  <SearchableSelect
                    options={digitalDesignCodes}
                    value={digitalDesignCode}
                    onChange={handleDigitalDesignCodeChange}
                    placeholder="Select digital design"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">COLORWAY</Label>
                  <SearchableSelect
                    key={`colorway-${digitalDesignCode}-${digitalDesignColorways.length}`}
                    options={digitalDesignColorways}
                    value={digitalDesignColorway}
                    onChange={handleDigitalDesignColorwayChange}
                    placeholder="Select colorway"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">DESIGN TYPE (AUTO)</Label>
                  <Input value={digitalDesignType} readOnly className="bg-gray-50 border-gray-300 text-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">CONSUMPTION</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={digitalDesignConsumption} 
                    onChange={(e) => setDigitalDesignConsumption(e.target.value)}
                    placeholder="Enter value"
                    className="text-sm"
                  />
                </div>
              </div>
              
              {/* Right side - Colorway Image (1/3 width) */}
              <div className="col-span-1">
                {digitalDesignColorwayImage ? (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-500">COLORWAY IMAGE</Label>
                    <div className="flex items-start">
                      <img 
                        src={digitalDesignColorwayImage} 
                        alt="Selected Colorway" 
                        className="w-auto h-[120px] object-contain rounded"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-400">COLORWAY IMAGE</Label>
                    <div className="flex items-start">
                      <p className="text-xs text-gray-400">Select colorway to view image</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Status */}
          <div className="space-y-4 p-5 border-2 border-black bg-black text-white">
            <h3 className="text-xs font-semibold tracking-wider">4. STATUS</h3>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-gray-300">CURRENT STATUS *</Label>
              <SearchableSelect
                options={availableStatusOptions}
                value={status}
                onChange={setStatus}
                placeholder="Select status"
              />
              {(initialStatus === 'FINISHED_GOOD' || status === 'FINISHED_GOOD') && (
                <p className="text-xs text-yellow-300 mt-1">
                  ⚠️ Finished goods can only move to: Alteration, Altered, Approved, or Rejected Post Sampling
                </p>
              )}
            </div>
          </div>

          {/* Section 5: Points of Measurement */}
          <div className="space-y-4 p-5 border-2 border-black">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-semibold tracking-wider text-gray-500">5. POINTS OF MEASUREMENT</h3>
            </div>
            
            {/* Size Selector - Applies to ALL POMs */}
            <div className="p-3 border border-gray-300 bg-gray-50">
              <div className="w-48 space-y-2">
                <Label className="text-xs font-semibold text-gray-500">SIZE (APPLIES TO ALL MEASUREMENTS)</Label>
                <SearchableSelect
                  options={SIZE_OPTIONS}
                  value={pomSize}
                  onChange={setPomSize}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button type="button" onClick={addPOM} className="gap-2 bg-black hover:bg-gray-800" size="sm">
                <Plus className="w-4 h-4" />
                Add POM
              </Button>
              <p className="text-sm font-bold">Size: {pomSize}</p>
            </div>

            {poms.map((pom, index) => (
              <div key={index} className="flex gap-3 items-end p-3 border border-gray-300 bg-gray-50">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">POINT NAME</Label>
                  <Input
                    value={pom.point_name}
                    onChange={(e) => updatePOM(index, 'point_name', e.target.value)}
                    placeholder="Point name"
                    className="text-sm"
                  />
                </div>
                <div className="w-32 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">MEASUREMENT</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={pom.measurement || ''}
                    onChange={(e) => updatePOM(index, 'measurement', parseFloat(e.target.value) || 0)}
                    placeholder="Enter value"
                    className="text-sm"
                  />
                </div>
                <div className="w-32 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">UOM</Label>
                  <SearchableSelect
                    options={UOM_OPTIONS}
                    value={pom.uom}
                    onChange={(val) => updatePOM(index, 'uom', val)}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePOM(index)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Section 6: Trims & Accessories */}
          <div className="space-y-4 p-5 border-2 border-black">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-semibold tracking-wider text-gray-500">6. TRIMS & ACCESSORIES</h3>
              <Button type="button" onClick={addTrim} className="gap-2 bg-black hover:bg-gray-800" size="sm">
                <Plus className="w-4 h-4" />
                Add Trim
              </Button>
            </div>
            {trims.map((trim, index) => (
              <div key={index} className="flex gap-3 items-end p-3 border border-gray-300 bg-gray-50">
                <div className="flex-1 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">TRIM CODE (LN-Z)</Label>
                  <SearchableSelect
                    options={trimCodes}
                    value={trim.trim_code}
                    onChange={(val) => handleTrimCodeChange(index, val)}
                    placeholder="Select trim"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">COLOR CODE</Label>
                  <SearchableSelect
                    options={trimColors[index]?.map(c => ({ 
                      value: c.color_code, 
                      label: `${c.color_code} - ${c.color_name}` 
                    })) || []}
                    value={trim.color_code}
                    onChange={(val) => handleTrimColorChange(index, val)}
                    placeholder="Select color"
                  />
                </div>
                <div className="w-32 space-y-2">
                  <Label className="text-xs font-semibold text-gray-500">QUANTITY</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={trim.quantity || ''}
                    onChange={(e) => updateTrim(index, 'quantity', parseFloat(e.target.value) || 0)}
                    placeholder="Enter value"
                    className="text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTrim(index)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Section 7: Image Uploads */}
          <div className="space-y-4 p-5 border-2 border-black">
            <h3 className="text-xs font-semibold tracking-wider text-gray-500">7. IMAGE UPLOADS</h3>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">INSPIRATION IMAGE</Label>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-400 p-4 text-center hover:border-black transition-colors">
                      <Upload className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-xs font-semibold">UPLOAD</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, setInspirationImage)}
                    />
                  </label>
                  {inspirationImage && (
                    <div className="relative">
                      <img src={getImageDisplayUrl(inspirationImage)} alt="Inspiration" className="w-full h-64 object-contain border-2 border-gray-300" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setInspirationImage('')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">SKETCH FRONT</Label>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-400 p-4 text-center hover:border-black transition-colors">
                      <Upload className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-xs font-semibold">UPLOAD</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, setSketchFrontImage)}
                    />
                  </label>
                  {sketchFrontImage && (
                    <div className="relative">
                      <img src={getImageDisplayUrl(sketchFrontImage)} alt="Sketch Front" className="w-full h-64 object-contain border-2 border-gray-300" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setSketchFrontImage('')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">SKETCH BACK</Label>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-400 p-4 text-center hover:border-black transition-colors">
                      <Upload className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-xs font-semibold">UPLOAD</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, setSketchBackImage)}
                    />
                  </label>
                  {sketchBackImage && (
                    <div className="relative">
                      <img src={getImageDisplayUrl(sketchBackImage)} alt="Sketch Back" className="w-full h-64 object-contain border-2 border-gray-300" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setSketchBackImage('')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500">FINISHED GOOD IMAGE</Label>
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-400 p-4 text-center hover:border-black transition-colors">
                      <Upload className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-xs font-semibold">UPLOAD</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, setFinishedGoodImage)}
                    />
                  </label>
                  {finishedGoodImage && (
                    <div className="relative">
                      <img src={getImageDisplayUrl(finishedGoodImage)} alt="Finished Good" className="w-full h-64 object-contain border-2 border-gray-300" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setFinishedGoodImage('')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 8: Comments */}
          <div className="space-y-4 p-5 border-2 border-black">
            <h3 className="text-xs font-semibold tracking-wider text-gray-500">8. COMMENTS</h3>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Enter any additional comments or notes..."
              className="w-full min-h-[100px] px-3 py-2 text-sm border-2 border-gray-300 focus:outline-none focus:border-black"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t-2 border-black">
            <Button type="button" variant="outline" onClick={onClose} className="border-2 border-gray-300">
              Cancel
            </Button>
            <Button type="submit" className="bg-black hover:bg-gray-800 text-white font-semibold">
              {editingDesign ? 'Update' : 'Create'} Design
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
